import React, { useState } from 'react'
import * as chatbotService from '@/services/chatbot.service'
import useToast from '@/hooks/useToast'
import CloseEyeIcon from '../Icons/CloseEyeIcon'
import EyeIcon from '../Icons/EyeIcon'

const BubblePassword = ({
  kb_id,
  setCookie,
  onUnlockSuccess,
  onBubble = true,
  loading: externalLoading = false,
  error = '',
  className = '',
  onClose,
  config,
  ...props
}) => {
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [validationError, setValidationError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { addErrorToast, addSuccessToast } = useToast()

  const loading = externalLoading || isLoading

  const handleUnlock = async () => {
    // Clear previous errors
    setValidationError('')
    setErrorMessage('')

    // Check if password is empty
    if (!password.trim()) {
      setValidationError('Password is required')
      return
    }

    setIsLoading(true)
    try {
      const response = await chatbotService.chatbotAuthentication(
        kb_id,
        password
      )
      if (response.status === 200) {
        addSuccessToast({ message: 'AI Chatbot unlocked successfully' })
        setCookie(response.data.session_cookie)
        setPassword('')
        onUnlockSuccess()
      }
    } catch (error) {
      if (error?.response?.data?.detail) {
        console.log('error', error?.response?.data?.detail)
        if (onBubble) {
          setErrorMessage(error?.response?.data?.detail)
        } else {
          addErrorToast({ message: error?.response?.data?.detail })
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    handleUnlock()
  }

  const handlePasswordChange = (e) => {
    setPassword(e.target.value)

    if (validationError) {
      setValidationError('')
    }
    if (errorMessage) {
      setErrorMessage('')
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleUnlock()
    }
  }

  return (
    <div className={`
      flex
      flex-col
      p-v3-lg
      gap-v3-lg
      w-full
      h-full
      ${className}
    `} {...props}>
     
      <div className="text-left font-inter-variable text-v3-h4 font-semibold text-v3-black-dark">
        <h2 className="">
          Welcome to the chat!
        </h2>
        <p className="">
          Enter the password to access.
        </p>
      </div>

      {/* Password Form */}
      <form onSubmit={handleSubmit} className="w-full ">
        <div className="space-y-v3-xxs">
          <label
            htmlFor="password"
            className="block text-v3-label text-v3-black-fixed-primary"
          >
          Password
          </label>

          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={password}
              onChange={handlePasswordChange}
              onKeyDown={handleKeyDown}
              disabled={loading}
              autoComplete="off"
              className={`
                w-full
                h-[40px]
                px-[12px]
                pr-[44px]
                py-[10px]
                rounded-v3-sm
                text-v3-black-fixed-primary
                placeholder-v3-black-placeholder
                text-v3-input
                outline-none
                border
                border-v3-black-placeholder
                ${(error || validationError || errorMessage) ? 'border-v3-red-primary' : ''}
                ${loading ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              style={{ backgroundColor: config?.surface_color || '#ffffff' }}
              placeholder="Enter password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-v3-black-secondary hover:text-v3-black-primary transition-colors disabled:opacity-50"
            >
              {showPassword ? (
                 <EyeIcon className="w-5 h-5" />
              ) : (
               
                <CloseEyeIcon className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Error Message */}
          <div className="h-[16px] flex items-start transition-all duration-200 ease-in-out">
            {(error || validationError || errorMessage) && (
              <p className="text-v3-red-dark font-inter-variable text-xs font-medium animate-fade-in">
                {error || validationError || errorMessage}
              </p>
            )}
          </div>
        </div>

        {/* Add spacing when error is present */}
        <div className={`transition-all duration-200 ease-in-out ${
          (error || validationError || errorMessage) ? 'mb-v3-sm' : 'mb-0'
        }`} />

       <div className='flex justify-end'>
         <button
          type="submit"
          disabled={loading}
          className={`
            w-[133px]
            h-[40px]
            gap-v3-xs
            px-v3-md
            py-v3-xs
            rounded-v3-xxl
            bg-[var(--dt-color-brand-100)]
            text-[#FFFFFF]
            font-medium
            text-sm
            font-inter-variable
            transition-all
            duration-200
            ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[var(--dt-color-brand-60)]'}
          `}
        >
          {loading ? 'Unlocking...' : 'Access Chat'}
        </button>
       </div>
      </form>
    </div>
  )
}

export default BubblePassword